/**
 * Presentation helpers shared across the archive screens.
 *
 * Extracted from the containers page because they are not components: keeping
 * them there meant every screen that formatted a byte count imported a page,
 * and react-refresh warns for exactly that reason.
 */

// Brand palette, kept clearly distinguishable: navy (primary), slate teal
// (secondary), blush (tertiary, light variant only — the pastel needs a tinted
// background and a deep text shade to stay legible).
//
// Classification is the customer's taxonomy, not ours — free text on the API.
// These maps describe the values BioNTech's delivery is configured with; they
// are presentation only. A classification nobody anticipated must render just
// as well, so both lookups fall back rather than assuming membership.
export const CLUSTER_LABEL: Record<string, string> = {
  A: "Application retirement & structured data",
  B: "Large-scale object & file archiving",
  C: "Enterprise records & content governance",
};

const CLUSTER_PALETTE = ["navy", "teal", "blush", "grape", "cyan", "orange"];
const KNOWN_COLOUR: Record<string, string> = { A: "navy", B: "teal", C: "blush" };

/** A stable colour for any classification. Configured values keep the colours
 *  the deck uses; anything else is hashed to the palette so it is at least
 *  consistent between screens rather than changing on every render. */
export function clusterColour(cluster: string): string {
  if (KNOWN_COLOUR[cluster]) return KNOWN_COLOUR[cluster]!;
  if (!cluster) return "gray";
  let hash = 0;
  for (const ch of cluster) hash = (hash * 31 + ch.charCodeAt(0)) % 997;
  return CLUSTER_PALETTE[hash % CLUSTER_PALETTE.length]!;
}

/** How a classification reads on screen. A single letter is one of the
 *  configured clusters and gets the "Cluster A" phrasing; anything longer is a
 *  name the customer chose and is shown as they typed it. */
export function clusterTitle(cluster: string): string {
  if (!cluster) return "Unclassified";
  return cluster.length === 1 ? `Cluster ${cluster}` : cluster;
}

export function formatBytes(value: number): string {
  if (!value) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.min(units.length - 1, Math.floor(Math.log(value) / Math.log(1024)));
  return `${(value / 1024 ** i).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

export function formatCount(value: number): string {
  return value.toLocaleString("en-GB");
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return "—";
  return new Date(value).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}
